/**
 * Hook for inspection execution (CRUD for inspection items)
 * 
 * Handles upserting item results and submitting inspections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route inspection-execution lifecycle writes through clock.
import { clock } from '@/utils/clock';
import { useToast } from '@/hooks/use-toast';

export type InspectionSeverity = 'PASS' | 'CAUTION' | 'FAIL' | 'NA';

export interface InspectionItemResult {
  id: string;
  ticket_inspection_id: string;
  template_item_id: string;
  shop_id: string;
  system_key: string;
  item_key: string;
  shared_key: string | null;
  severity: InspectionSeverity | null;
  note: string | null;
  result: Record<string, unknown> | null;
  measurements: Record<string, unknown> | null;
  completed_by: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InspectionEvidence {
  id: string;
  shop_id: string;
  ticket_inspection_item_id: string;
  evidence_type: 'photo' | 'video' | 'document';
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  captured_by: string;
  captured_at: string;
  meta: Record<string, unknown>;
}

export interface UpsertItemParams {
  templateItemId: string;
  systemKey: string;
  itemKey: string;
  sharedKey?: string | null;
  severity?: InspectionSeverity | null;
  note?: string | null;
  result?: Record<string, unknown> | null;
  measurements?: Record<string, unknown> | null;
}

interface SubmitResult {
  success: boolean;
  error?: string;
  message?: string;
  count?: number;
}

interface UseInspectionExecutionResult {
  /** All item results for this inspection */
  itemResults: InspectionItemResult[];
  /** All evidence for this inspection */
  evidence: InspectionEvidence[];
  /** Map of template_item_id to result for O(1) lookup */
  resultsByItemId: Map<string, InspectionItemResult>;
  /** Map of item_id to evidence array */
  evidenceByItemId: Map<string, InspectionEvidence[]>;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether editing is allowed (inspection is IN_PROGRESS) */
  isEditable: boolean;
  /** Upsert an item result */
  upsertItemResult: (params: UpsertItemParams) => Promise<void>;
  /** Whether upsert is in progress */
  isUpserting: boolean;
  /** Submit the inspection */
  submitInspection: () => Promise<SubmitResult>;
  /** Whether submission is in progress */
  isSubmitting: boolean;
  /** Refetch data */
  refetch: () => void;
}

/**
 * Hook for managing inspection item execution
 * 
 * @param inspectionId - The inspection ID
 * @param shopId - The shop ID (derived from inspection)
 * @param inspectionStatus - The current inspection status
 */
export function useInspectionExecution(
  inspectionId: string | undefined,
  shopId: string | undefined,
  inspectionStatus: string | undefined
): UseInspectionExecutionResult {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const itemsQueryKey = ['ticket-inspection-items', inspectionId];
  const evidenceQueryKey = ['ticket-inspection-evidence', inspectionId];

  // Fetch item results
  const {
    data: itemResults,
    isLoading: itemsLoading,
    error: itemsError,
    refetch: refetchItems,
  } = useQuery({
    queryKey: itemsQueryKey,
    queryFn: async () => {
      if (!inspectionId) return [];

      const { data, error } = await supabase
        .from('ticket_inspection_items')
        .select('*')
        .eq('ticket_inspection_id', inspectionId)
        .order('created_at');

      if (error) throw error;
      return data as InspectionItemResult[];
    },
    enabled: !!inspectionId,
  });

  // Fetch evidence
  const {
    data: evidence,
    isLoading: evidenceLoading,
    error: evidenceError,
    refetch: refetchEvidence,
  } = useQuery({
    queryKey: evidenceQueryKey,
    queryFn: async () => {
      if (!inspectionId) return [];

      // Get all item IDs for this inspection first
      const itemIds = itemResults?.map(r => r.id) || [];
      if (itemIds.length === 0) return [];

      const { data, error } = await supabase
        .from('ticket_inspection_evidence')
        .select('*')
        .in('ticket_inspection_item_id', itemIds)
        .order('captured_at');

      if (error) throw error;
      return data as InspectionEvidence[];
    },
    enabled: !!inspectionId && (itemResults?.length ?? 0) > 0,
  });

  // Build lookup maps
  const resultsByItemId = new Map<string, InspectionItemResult>();
  (itemResults || []).forEach(result => {
    resultsByItemId.set(result.template_item_id, result);
  });

  const evidenceByItemId = new Map<string, InspectionEvidence[]>();
  (evidence || []).forEach(ev => {
    const existing = evidenceByItemId.get(ev.ticket_inspection_item_id) || [];
    existing.push(ev);
    evidenceByItemId.set(ev.ticket_inspection_item_id, existing);
  });

  // Upsert mutation
  const upsertMutation = useMutation({
    mutationFn: async (params: UpsertItemParams) => {
      if (!inspectionId || !shopId) {
        throw new Error('Inspection ID and Shop ID are required');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const payload = {
        ticket_inspection_id: inspectionId,
        template_item_id: params.templateItemId,
        shop_id: shopId,
        system_key: params.systemKey,
        item_key: params.itemKey,
        shared_key: params.sharedKey ?? null,
        severity: params.severity ?? null,
        note: params.note ?? null,
        result: params.result ?? null,
        measurements: params.measurements ?? null,
        completed_by: user.id,
        completed_at: clock.now().toISOString(),
        updated_at: clock.now().toISOString(),
      };

      const { error } = await (supabase
        .from('ticket_inspection_items') as any)
        .upsert(payload, {
          onConflict: 'ticket_inspection_id,template_item_id',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemsQueryKey });
    },
    onError: (error) => {
      toast({
        title: 'Error saving inspection item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async (): Promise<SubmitResult> => {
      if (!inspectionId) {
        throw new Error('Inspection ID is required');
      }

      const { data, error } = await supabase.rpc('submit_ticket_inspection', {
        p_inspection_id: inspectionId,
      });

      if (error) throw error;
      return data as unknown as SubmitResult;
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Inspection submitted',
          description: 'The inspection has been submitted for review.',
        });
        // Invalidate to refresh status
        queryClient.invalidateQueries({ queryKey: ['ticket-inspection'] });
      } else {
        toast({
          title: 'Cannot submit inspection',
          description: result.message || 'Please check all required items.',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error submitting inspection',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const isEditable = inspectionStatus === 'IN_PROGRESS';

  return {
    itemResults: itemResults || [],
    evidence: evidence || [],
    resultsByItemId,
    evidenceByItemId,
    isLoading: itemsLoading || evidenceLoading,
    error: (itemsError || evidenceError) as Error | null,
    isEditable,
    upsertItemResult: async (params) => {
      await upsertMutation.mutateAsync(params);
    },
    isUpserting: upsertMutation.isPending,
    submitInspection: async () => {
      return await submitMutation.mutateAsync();
    },
    isSubmitting: submitMutation.isPending,
    refetch: () => {
      refetchItems();
      refetchEvidence();
    },
  };
}
